import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Spinner, Alert } from '@openedx/paragon';
import { FormattedMessage, useIntl } from '@edx/frontend-platform/i18n';
import { getFeatureToggle } from '@blend-ed/blendxplatform/utils';

import { useFetchLearnerDocuments } from '../../gql';
import emptyNoteIcon from '../../assets/empty-note-icon.png';
import LibraryHeader from './components/LibraryHeader';
import FileTable from './components/FileTable';
import FileGrid from './components/FileGrid';
import FilePreviewModal from './components/FilePreviewModal';
import { normalizeDocument } from './data/files';
import messages from './messages';
import './Library.scss';
import './components/LibraryHeader.scss';
import './components/FileTable.scss';
import './components/FileCard.scss';
import './components/FileGrid.scss';
import './components/FilePreviewModal.scss';

const Library = () => {
  const { formatMessage } = useIntl();
  const [searchValue, setSearchValue] = useState('');
  const [sortOrder, setSortOrder] = useState('latest');
  const [viewMode, setViewMode] = useState('table');
  const [previewState, setPreviewState] = useState({ isOpen: false, file: null });

  const { documents, loading, error } = useFetchLearnerDocuments();

  const featuresToggled = getFeatureToggle();
  const libraryEnabled = featuresToggled.features_learners_library === true;

  const visibleFiles = useMemo(() => {
    const normalized = documents.map(normalizeDocument);
    const term = searchValue.trim().toLowerCase();
    const filtered = term
      ? normalized.filter((f) => f.name.toLowerCase().includes(term))
      : normalized;
    return [...filtered].sort((a, b) => {
      const diff = new Date(b.updatedAtRaw) - new Date(a.updatedAtRaw);
      return sortOrder === 'latest' ? diff : -diff;
    });
  }, [documents, searchValue, sortOrder]);

  const previewFile = (file) => {
    if (!file?.url) { return; }
    setPreviewState({ isOpen: true, file });
  };

  const closePreview = () => setPreviewState((prev) => ({ ...prev, isOpen: false }));

  // Wait for the org toggle to resolve before deciding, otherwise a direct hit
  // to /library would briefly flash content then redirect.
  if (featuresToggled.loading) {
    return (
      <div className="library-page__loading">
        <Spinner
          animation="border"
          variant="primary"
          screenReaderText={formatMessage(messages.loading)}
        />
      </div>
    );
  }

  if (!libraryEnabled) {
    return <Navigate to="/" replace />;
  }

  const downloadFile = async (file) => {
    if (!file?.url) { return; }
    try {
      const response = await fetch(file.url);
      if (!response.ok) { throw new Error('Network response was not ok'); }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.name || 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(file.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="library-page">
      <div className="library-page__top">
        <LibraryHeader
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          sortOrder={sortOrder}
          onSortChange={setSortOrder}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />

        {loading && (
          <div className="library-page__loading">
            <Spinner
              animation="border"
              variant="primary"
              screenReaderText={formatMessage(messages.loading)}
            />
          </div>
        )}

        {!loading && error && (
          <Alert variant="danger">
            <FormattedMessage {...messages.loadError} />
          </Alert>
        )}

        {!loading && !error && visibleFiles.length === 0 && (
          <div className="library-empty">
            <img
              src={emptyNoteIcon}
              alt=""
              className="library-empty__icon"
            />
            <div className="library-empty__text">
              <span className="library-empty__title">
                <FormattedMessage {...messages.emptyTitle} />
              </span>
              <p className="library-empty__description">
                <FormattedMessage {...messages.emptyDescription} />
              </p>
            </div>
          </div>
        )}

        {!loading && !error && visibleFiles.length > 0 && (
          viewMode === 'table'
            ? <FileTable files={visibleFiles} onPreview={previewFile} onDownload={downloadFile} />
            : <FileGrid files={visibleFiles} onPreview={previewFile} onDownload={downloadFile} />
        )}
      </div>

      <FilePreviewModal
        file={previewState.file}
        isOpen={previewState.isOpen}
        onClose={closePreview}
        onDownload={downloadFile}
      />
    </div>
  );
};

export default Library;
